import english from './i18n/en-US.json';
import french from './i18n/fr-FR.json';
import { DateFormat, Locale } from './types/A3';
import { StartupMode } from './types/AppSettings';
import {
  APP_FOOTER_HEIGHT,
  APP_FOOTER_TRIAL_EXTRA_HEIGHT,
  HistoryDataFetchLimit,
  WeekStartsOnDay,
} from './config';

export type { WeekStartsOnDay };

export const WEEK_STARTS_ON_OPTIONS: WeekStartsOnDay[] = [1, 2, 3, 4, 5, 6, 0];

export function isValidWeekStartsOn(value: unknown): value is WeekStartsOnDay {
  if (value === '' || value === null || value === undefined) return false;
  const parsed = typeof value === 'number' ? value : Number(String(value));
  return Number.isInteger(parsed) && !Number.isNaN(parsed) && parsed >= 0 && parsed <= 6;
}

const MIXPANEL_DAY_EN: Record<WeekStartsOnDay, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

/** English weekday name for Mixpanel (e.g. `"Monday"`). */
export function weekStartsOnToMixpanelDay(day: WeekStartsOnDay): string {
  return MIXPANEL_DAY_EN[day];
}

export const getTranslation = (locale: Locale) => {
  switch (locale) {
    case 'fr-FR':
      return french;
    default:
      return english;
  }
};

export function localeToMixpanelLanguage(locale: Locale): 'English' | 'French' {
  return locale === 'fr-FR' ? 'French' : 'English';
}

const START_PAGE_BY_MODE: Record<StartupMode, string> = {
  'landing-page': 'Landing Page',
  example: 'Example Data',
  'last-loaded': 'Last Loaded',
};

export function startupModeToMixpanelStartPage(mode: StartupMode): string {
  return START_PAGE_BY_MODE[mode] ?? 'Landing Page';
}

function getShowSidebarFromStorage(): boolean {
  try {
    const generalDataLS = localStorage.getItem('general-data');
    if (!generalDataLS) return true;
    return !!JSON.parse(generalDataLS).data?.global?.isSidebarOpen;
  } catch {
    return true;
  }
}

export type SessionMixPanelInput = {
  dateFormat: DateFormat;
  locale: Locale;
  isDarkMode: boolean;
  weekStartsOn: WeekStartsOnDay;
  historicalDataFetchLimit: HistoryDataFetchLimit;
  startupMode: StartupMode;
  favoriteCount?: number;
};

export const sessionMixPanelData = (input: SessionMixPanelInput, eventType: 'start' | 'end') => {
  const payload = {
    Language: localeToMixpanelLanguage(input.locale),
    'Color Scheme': input.isDarkMode ? 'Dark' : 'Light',
    'Date Format': input.dateFormat,
    'Start Page': startupModeToMixpanelStartPage(input.startupMode),
    'Show Sidebar': getShowSidebarFromStorage(),
    'Start of Week': weekStartsOnToMixpanelDay(input.weekStartsOn),
    'Historical Fetch Limit': input.historicalDataFetchLimit,
  };

  if (eventType === 'end') {
    return { ...payload, 'Favorite Count': input.favoriteCount ?? 0 };
  }

  return payload;
};

export const calculateFooterHeight = (isTrialActive: boolean) => {
  if (isTrialActive) return APP_FOOTER_HEIGHT + APP_FOOTER_TRIAL_EXTRA_HEIGHT;
  return APP_FOOTER_HEIGHT;
};
